/**
 * Malaysian location constants — single source of truth for state/district data
 * Used by client, admin, and server for location inputs and matching
 */

export const MALAYSIAN_STATE_VALUES = [
  'johor',
  'kedah',
  'kelantan',
  'kuala_lumpur',
  'labuan',
  'malacca',
  'negeri_sembilan',
  'pahang',
  'penang',
  'perak',
  'perlis',
  'putrajaya',
  'sabah',
  'sarawak',
  'selangor',
  'terengganu',
] as const;

export type MalaysianState = (typeof MALAYSIAN_STATE_VALUES)[number];

/** Display labels for each state value */
export const STATE_LABELS: Record<MalaysianState, string> = {
  johor: 'Johor',
  kedah: 'Kedah',
  kelantan: 'Kelantan',
  kuala_lumpur: 'Kuala Lumpur',
  labuan: 'Labuan',
  malacca: 'Malacca',
  negeri_sembilan: 'Negeri Sembilan',
  pahang: 'Pahang',
  penang: 'Penang',
  perak: 'Perak',
  perlis: 'Perlis',
  putrajaya: 'Putrajaya',
  sabah: 'Sabah',
  sarawak: 'Sarawak',
  selangor: 'Selangor',
  terengganu: 'Terengganu',
};

/** Districts keyed by state value */
export const STATE_DISTRICTS: Record<MalaysianState, string[]> = {
  johor: ['Batu Pahat', 'Johor Bahru', 'Kluang', 'Kota Tinggi', 'Kulai', 'Mersing', 'Muar', 'Pontian', 'Segamat', 'Tangkak'],
  kedah: ['Baling', 'Bandar Baharu', 'Kota Setar', 'Kuala Muda', 'Kubang Pasu', 'Kulim', 'Langkawi', 'Padang Terap', 'Pendang', 'Pokok Sena', 'Sik', 'Yan'],
  kelantan: ['Bachok', 'Gua Musang', 'Jeli', 'Kota Bharu', 'Kuala Krai', 'Machang', 'Pasir Mas', 'Pasir Puteh', 'Tanah Merah', 'Tumpat'],
  kuala_lumpur: ['Kuala Lumpur'],
  labuan: ['Labuan'],
  malacca: ['Alor Gajah', 'Jasin', 'Melaka Tengah'],
  negeri_sembilan: ['Jelebu', 'Jempol', 'Kuala Pilah', 'Port Dickson', 'Rembau', 'Seremban', 'Tampin'],
  pahang: ['Bentong', 'Bera', 'Cameron Highlands', 'Jerantut', 'Kuantan', 'Lipis', 'Maran', 'Pekan', 'Raub', 'Rompin', 'Temerloh'],
  penang: ['Barat Daya', 'Seberang Perai Selatan', 'Seberang Perai Tengah', 'Seberang Perai Utara', 'Timur Laut'],
  perak: ['Bagan Datuk', 'Batang Padang', 'Hilir Perak', 'Hulu Perak', 'Kampar', 'Kerian', 'Kinta', 'Kuala Kangsar', 'Larut, Matang dan Selama', 'Manjung', 'Muallim', 'Perak Tengah'],
  perlis: ['Perlis'],
  putrajaya: ['Putrajaya'],
  sabah: ['Beaufort', 'Beluran', 'Keningau', 'Kota Belud', 'Kota Kinabalu', 'Kota Marudu', 'Kuala Penyu', 'Kudat', 'Kunak', 'Lahad Datu', 'Nabawan', 'Papar', 'Penampang', 'Pitas', 'Ranau', 'Sandakan', 'Semporna', 'Sipitang', 'Tambunan', 'Tawau', 'Telupid', 'Tenom', 'Tongod', 'Tuaran'],
  sarawak: ['Asajaya', 'Betong', 'Bintulu', 'Dalat', 'Daro', 'Julau', 'Kabong', 'Kanowit', 'Kapit', 'Kuching', 'Lawas', 'Limbang', 'Lubok Antu', 'Lundu', 'Maradong', 'Marudi', 'Matu', 'Miri', 'Mukah', 'Pakan', 'Pusa', 'Samarahan', 'Sarikei', 'Saratok', 'Sebauh', 'Selangau', 'Serian', 'Sibu', 'Simunjan', 'Song', 'Sri Aman', 'Subis', 'Tatau', 'Tanjung Manis'],
  selangor: ['Gombak', 'Hulu Langat', 'Hulu Selangor', 'Klang', 'Kuala Langat', 'Kuala Selangor', 'Petaling', 'Sabak Bernam', 'Sepang'],
  terengganu: ['Besut', 'Dungun', 'Hulu Terengganu', 'Kemaman', 'Kuala Nerus', 'Kuala Terengganu', 'Marang', 'Setiu'],
};

/** Get districts for a given state value */
export const getDistrictsByState = (state: MalaysianState): string[] =>
  STATE_DISTRICTS[state] ?? [];

/** State options formatted for SearchableDropdown / select components */
export const STATE_OPTIONS = MALAYSIAN_STATE_VALUES.map((value) => ({
  value,
  label: STATE_LABELS[value],
}));
